import React, { useEffect, useState } from "react";
import { Order } from "../../Interfaces/Orders";
import OrderDetailsModal from "./OrderDetailsModal"; // Import the new modal component.
import "./ShippingDisplay.css";
import config from "../../config";
import fetchWithAuth from "../../services/fetchWithAuth";
import PrintComponent from "../PrintableComponent";
import { renderOrderPrintoutToString } from "./OrderPrintout";
import PopupModel from "../Popup/PopupModel";
import { usePopup } from "../Popup/PopupContext";
import ShippingUI from "./ShippingUI";

const BASE_API = config.API_BASE_URL;

interface ShippingTypeDisplayComponentProps {
  header: string;
  order_type: string;
  fundraiser_id: number;
}

const ShippingTypeDisplayComponent: React.FC<
  ShippingTypeDisplayComponentProps
> = ({ header, order_type, fundraiser_id }) => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [isModalOpen, setModalOpen] = useState(false);
  const [freeShippingThreshold, setFreeShippingThreshold] = useState<
    number | null
  >(null);
  const [baseShippingCost, setBaseShippingCost] = useState<number | null>(null);
  const { showPopup, hidePopup } = usePopup();

  // Function to fetch orders
  const fetchOrders = async () => {
    let url = BASE_API + `orders/?shipping=${order_type}&status=processing`;

    // Append the 'fundraiser' query parameter only if fundraiserId is not 0
    if (fundraiser_id !== 0) {
      url += `&fundraiser=${fundraiser_id}`;
    }

    const response = await fetchWithAuth(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      throw new Error(`Error fetching items: ${response.statusText}`);
    }

    const data: Order[] = await response.json();
    setOrders(data);
  };

  useEffect(() => {
    const fetchFreeShippingThreshold = async () => {
      try {
        const response = await fetch(
          `${config.API_BASE_URL}config-options/free_shipping_threshold`
        );
        const data = await response.json();
        setFreeShippingThreshold(Number(data[0].config_value));
      } catch (error) {
        console.error("Error fetching free shipping threshold:", error);
      }
    };
    const fetchBaseShipping = async () => {
      try {
        const response = await fetch(
          `${config.API_BASE_URL}config-options/base_shipping_cost`
        );
        const data = await response.json();
        setBaseShippingCost(Number(data[0].config_value));
      } catch (error) {
        console.error("Error fetching base shipping:", error);
      }
    };

    fetchBaseShipping();
    fetchFreeShippingThreshold();
  }, []);

  // Fetch orders when the component mounts or when order_type or fundraiser_id changes
  useEffect(() => {
    fetchOrders();
  }, [order_type, fundraiser_id]);

  const handleOrderClick = (order: Order) => {
    setSelectedOrder(order);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setSelectedOrder(null);
  };

  const handleShipOrderClick = async (order: Order) => {
    const shipOrder = new PopupModel(
      `Shipping - Order ID ${order.order_id}`, // header
      (
        <div>
          {order?.shipping_info ? (
            <ShippingUI
              shipToName={order.shipping_info.ship_to_name}
              shipToAddress1={order.shipping_info.street_address}
              shipToAddress2={order.shipping_info.apt_suite || ""}
              shipToCity={order.shipping_info.city}
              shipToState={order.shipping_info.state}
              shipToZip={order.shipping_info.zip_code}
              shipToCountry="US"
            />
          ) : (
            <div>
              <p style={{ color: "gray", textAlign: "center" }}>
                No shipping info detected
              </p>
            </div>
          )}
        </div> //body
      ),
      [
        {
          label: "Mark as Shipped",
          onClick: () => {
            handleNextStep(order.order_id);
            hidePopup();
          },
        },
      ],
      "40vw" //width
    );

    showPopup(
      shipOrder.header,
      shipOrder.body,
      shipOrder.buttons,
      shipOrder.width
    );
  };

  const handleNextStep = async (orderNumber: number) => {
    const url = `${BASE_API}orders/${orderNumber}/status/`;
    const data = {
      new_status: "completed", // Need to figure out some logic here for next step
    };
    try {
      const response = await fetchWithAuth(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        throw new Error(`Error: ${response.statusText}`);
      }

      const responseData = await response.json(); // Parse the JSON response

      // Refresh orders after successfully updating the status
      await fetchOrders();
    } catch (error) {
      console.error("Error making POST request:", error);
    }
  };

  return (
    <div className="warehouse-order-display-container">
      <h2 className="warehouse-order-display-header">{header}</h2>

      <div className="warehouse-order-display-row-header warehouse-order-display-grid">
        <span className="warehouse-order-display-row-header-item">
          Order Number
        </span>
        <span className="warehouse-order-display-row-header-item">
          Order Date
        </span>
        <span className="warehouse-order-display-row-header-item">
          Order Status
        </span>
        <span className="warehouse-order-display-row-header-item">
          Item Count
        </span>
        <span className="warehouse-order-display-row-header-item">
          Next Step
        </span>
        <span className="warehouse-order-display-row-header-item">Print</span>
        <span className="warehouse-order-display-row-header-item">Ship</span>
      </div>

      {orders.map((order) => (
        <div
          key={order.order_id}
          className={`warehouse-order-display-order-line warehouse-order-display-grid ${
            order.is_highlighted ? "warehouse-order-display-highlighted" : ""
          }`}
        >
          <span
            className="warehouse-order-display-order-number warehouse-order-display-clickable"
            onClick={() => handleOrderClick(order)}
          >
            {order.order_id}
          </span>
          <span className="warehouse-order-display-order-date">
            {order.created_at.split("T")[0]} {/* Split to get only the date */}
          </span>
          <span className="warehouse-order-display-order-status">
            {order.latest_status.status}
          </span>
          <span className="warehouse-order-display-item-count">
            Items: {order.item_count}
          </span>
          <div className="warehouse-order-display-order-buttons">
            <button
              onClick={() => handleNextStep(order.order_id)}
              className="warehouse-order-display-advance-button"
            >
              Next Step
            </button>
            {freeShippingThreshold && baseShippingCost && (
              <PrintComponent
                content={renderOrderPrintoutToString(
                  order,
                  baseShippingCost,
                  freeShippingThreshold
                )}
                styles=""
              />
            )}
            {order_type === "home" && (
              <button
                onClick={() => {
                  handleShipOrderClick(order);
                }}
                className="warehouse-order-display-advance-button"
              >
                Ship Order
              </button>
            )}
          </div>
        </div>
      ))}

      <OrderDetailsModal
        order={selectedOrder}
        isOpen={isModalOpen}
        onClose={closeModal}
      />
    </div>
  );
};

export default ShippingTypeDisplayComponent;
